import { useCallback, useContext, useMemo } from "react";
import {
    ReconciliationUnmatchedBehavior,
    TransactionsPairForReconciliation,
} from "@common/contracts/reconciliation.contract";
import { RevertIcon } from "@icons";
import { Menu } from "@design-system/Menu/Menu.lib";
import {
    getStatmentTransactionId,
    pairHasDifference,
} from "@common/helpers/reconciliation";
import { canTakeStatementDate } from "@common/helpers/reconciliationCashItems";
import { Button } from "@design-system/Button/Button";
import { ReconciliationContext } from "./Reconciliation.context";
import {
    APPROVE_RECONCILIATION_CHANGE_BUTTON_CLASSNAME,
    pairDifferenceIsAccepted,
} from "./helpers";
import styles from "./Reconciliation.module.scss";

interface Props {
    pair: TransactionsPairForReconciliation;
    onUpdatePair: (pair: TransactionsPairForReconciliation) => void;
    isBalanceReconciliation: boolean;
}

export const ReconciliationAction: React.FC<Props> = ({
    pair,
    onUpdatePair,
    isBalanceReconciliation,
}) => {
    const {
        setTransactionToMatch,
        financialTransactionsToRemoveIdsSet,
        statementTransactionsToAddIdsSet,
        financialTransactionIdsIgnoringDateChanges,
        registerFinancialTransactionToRemove,
        registerStatementTransactionToAdd,
        unregisterFinancialTransactionToRemove,
        unregisterStatementTransactionToAdd,
        registerNotReconciledFinancialTransaction,
        unregisterNotReconciledFinancialTransaction,
        registerFinancialTransactionIdIgnoringDateChange,
        unregisterFinancialTransactionIdIgnoringDateChange,
        isReconciliationComplete,
        notReconciledFinancialTransactionsIdsSet,
        financialTransactionsFromPreviousPeriodIds,
        registerFinancialTransactionFromPreviousPeriod,
        unregisterFinancialTransactionFromPreviousPeriod,
    } = useContext(ReconciliationContext);
    const hasDifference = pairHasDifference(pair);

    const isAccepted = useMemo(
        () =>
            pairDifferenceIsAccepted({
                pair,
                financialTransactionsToRemoveIdsSet,
                statementTransactionsToAddIdsSet,
                notReconciledFinancialTransactionsIdsSet,
                financialTransactionsFromPreviousPeriodIds,
            }),
        [
            financialTransactionsToRemoveIdsSet,
            statementTransactionsToAddIdsSet,
            notReconciledFinancialTransactionsIdsSet,
            financialTransactionsFromPreviousPeriodIds,
            pair,
        ],
    );

    const acceptAction = () => {
        if (pair.fromAccount) {
            if (
                pair.fromAccount.unmatchedBehavior ===
                ReconciliationUnmatchedBehavior.REMOVE
            ) {
                registerFinancialTransactionToRemove(
                    pair.fromAccount.financialTransactionId,
                );
            }
            if (
                pair.fromAccount.unmatchedBehavior ===
                ReconciliationUnmatchedBehavior.NOT_RECONCILED
            ) {
                registerNotReconciledFinancialTransaction(
                    pair.fromAccount.financialTransactionId,
                );
            }
            if (
                pair.fromAccount.unmatchedBehavior ===
                ReconciliationUnmatchedBehavior.FROM_PREVIOUS_PERIOD
            ) {
                registerFinancialTransactionFromPreviousPeriod(
                    pair.fromAccount.financialTransactionId,
                );
            }
        }
        if (pair.fromStatement) {
            registerStatementTransactionToAdd(
                getStatmentTransactionId(pair.fromStatement),
            );
        }
    };

    const undoAction = () => {
        if (pair.fromAccount) {
            if (
                pair.fromAccount.unmatchedBehavior ===
                ReconciliationUnmatchedBehavior.REMOVE
            ) {
                unregisterFinancialTransactionToRemove(
                    pair.fromAccount.financialTransactionId,
                );
            }
            if (
                pair.fromAccount.unmatchedBehavior ===
                ReconciliationUnmatchedBehavior.NOT_RECONCILED
            ) {
                unregisterNotReconciledFinancialTransaction(
                    pair.fromAccount.financialTransactionId,
                );
            }
            if (
                pair.fromAccount.unmatchedBehavior ===
                ReconciliationUnmatchedBehavior.FROM_PREVIOUS_PERIOD
            ) {
                unregisterFinancialTransactionFromPreviousPeriod(
                    pair.fromAccount.financialTransactionId,
                );
            }
        }
        if (pair.fromStatement) {
            unregisterStatementTransactionToAdd(
                getStatmentTransactionId(pair.fromStatement),
            );
        }
    };

    const message = useMemo(() => {
        if (
            pair.fromAccount &&
            pair.fromAccount.unmatchedBehavior ===
                ReconciliationUnmatchedBehavior.REMOVE
        ) {
            return isAccepted ? "Transaction deleted" : "Delete transaction";
        }
        if (
            pair.fromAccount &&
            pair.fromAccount.unmatchedBehavior ===
                ReconciliationUnmatchedBehavior.NOT_RECONCILED
        ) {
            return isAccepted ? "Marked unreconciled" : "Mark unreconciled";
        }
        if (
            pair.fromAccount &&
            pair.fromAccount.unmatchedBehavior ===
                ReconciliationUnmatchedBehavior.FROM_PREVIOUS_PERIOD
        ) {
            return isAccepted
                ? "Moved to prior period"
                : "Move to prior period";
        }
        if (pair.fromStatement) {
            return isAccepted ? "Transaction added" : "Add transaction";
        }
        return "";
    }, [pair, isAccepted]);

    const findTransaction = useCallback(() => {
        if (pair.fromStatement) {
            setTransactionToMatch({
                statementTransactionToMatch: pair.fromStatement,
                currentlyMatchedFinancialTransactionId:
                    pair.fromAccount?.financialTransactionId,
            });
        }
    }, [
        pair.fromStatement,
        pair.fromAccount?.financialTransactionId,
        setTransactionToMatch,
    ]);

    const switchToRemoval = useCallback(() => {
        if (pair.fromAccount) {
            onUpdatePair({
                ...pair,
                fromAccount: {
                    ...pair.fromAccount,
                    unmatchedBehavior: ReconciliationUnmatchedBehavior.REMOVE,
                },
            });
            unregisterNotReconciledFinancialTransaction(
                pair.fromAccount.financialTransactionId,
            );
            unregisterFinancialTransactionFromPreviousPeriod(
                pair.fromAccount.financialTransactionId,
            );
            registerFinancialTransactionToRemove(
                pair.fromAccount.financialTransactionId,
            );
        }
    }, [
        pair,
        onUpdatePair,
        unregisterNotReconciledFinancialTransaction,
        unregisterFinancialTransactionFromPreviousPeriod,
        registerFinancialTransactionToRemove,
    ]);
    const switchToNotReconciled = useCallback(() => {
        if (pair.fromAccount) {
            onUpdatePair({
                ...pair,
                fromAccount: {
                    ...pair.fromAccount,
                    unmatchedBehavior:
                        ReconciliationUnmatchedBehavior.NOT_RECONCILED,
                },
            });
            unregisterFinancialTransactionToRemove(
                pair.fromAccount.financialTransactionId,
            );
            unregisterFinancialTransactionFromPreviousPeriod(
                pair.fromAccount.financialTransactionId,
            );
            registerNotReconciledFinancialTransaction(
                pair.fromAccount.financialTransactionId,
            );
        }
    }, [
        pair,
        onUpdatePair,
        unregisterFinancialTransactionToRemove,
        unregisterFinancialTransactionFromPreviousPeriod,
        registerNotReconciledFinancialTransaction,
    ]);

    const switchToFromPreviousPeriod = useCallback(() => {
        if (pair.fromAccount) {
            onUpdatePair({
                ...pair,
                fromAccount: {
                    ...pair.fromAccount,
                    unmatchedBehavior:
                        ReconciliationUnmatchedBehavior.FROM_PREVIOUS_PERIOD,
                },
            });
            unregisterFinancialTransactionToRemove(
                pair.fromAccount.financialTransactionId,
            );
            unregisterNotReconciledFinancialTransaction(
                pair.fromAccount.financialTransactionId,
            );
            registerFinancialTransactionFromPreviousPeriod(
                pair.fromAccount.financialTransactionId,
            );
        }
    }, [
        pair,
        onUpdatePair,
        unregisterFinancialTransactionToRemove,
        unregisterNotReconciledFinancialTransaction,
        registerFinancialTransactionFromPreviousPeriod,
    ]);

    const dateChangeIsIgnored = useMemo(
        () =>
            pair.fromAccount &&
            financialTransactionIdsIgnoringDateChanges.includes(
                pair.fromAccount.financialTransactionId,
            ),
        [pair.fromAccount, financialTransactionIdsIgnoringDateChanges],
    );

    const toggleDateChangeIgnoring = useCallback(() => {
        if (!pair.fromAccount) {
            return;
        }
        if (dateChangeIsIgnored) {
            unregisterFinancialTransactionIdIgnoringDateChange(
                pair.fromAccount.financialTransactionId,
            );
        } else {
            registerFinancialTransactionIdIgnoringDateChange(
                pair.fromAccount.financialTransactionId,
            );
        }
    }, [
        dateChangeIsIgnored,
        pair.fromAccount,
        unregisterFinancialTransactionIdIgnoringDateChange,
        registerFinancialTransactionIdIgnoringDateChange,
    ]);
    const allowSwitchingToRemoval =
        hasDifference &&
        pair.fromAccount &&
        pair.fromAccount.unmatchedBehavior !==
            ReconciliationUnmatchedBehavior.REMOVE;
    const allowSwitchingToNotReconciled =
        hasDifference &&
        pair.fromAccount &&
        pair.fromAccount.unmatchedBehavior !==
            ReconciliationUnmatchedBehavior.NOT_RECONCILED;
    const allowSwitchingToFromPreviousPeriod =
        hasDifference &&
        pair.fromAccount &&
        isBalanceReconciliation &&
        pair.fromAccount.unmatchedBehavior !==
            ReconciliationUnmatchedBehavior.FROM_PREVIOUS_PERIOD;
    const canRejectDateChange =
        pair.fromAccount &&
        pair.fromStatement &&
        canTakeStatementDate(pair.fromAccount, pair.fromStatement);
    const enableContextMenu =
        allowSwitchingToRemoval ||
        allowSwitchingToNotReconciled ||
        allowSwitchingToFromPreviousPeriod ||
        pair.fromStatement ||
        canRejectDateChange;

    return (
        <>
            {hasDifference && (
                <>
                    <span className={styles.pairActionMessage}>{message}</span>
                    {!isReconciliationComplete && (
                        <>
                            {isAccepted ? (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={undoAction}
                                    className={
                                        APPROVE_RECONCILIATION_CHANGE_BUTTON_CLASSNAME
                                    }
                                    iconOnly
                                    aria-label="Revert reconciliation change"
                                >
                                    <RevertIcon />
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={acceptAction}
                                    className={
                                        APPROVE_RECONCILIATION_CHANGE_BUTTON_CLASSNAME
                                    }
                                >
                                    Accept
                                </Button>
                            )}
                        </>
                    )}
                </>
            )}
            {enableContextMenu && !isReconciliationComplete && (
                <Menu.More>
                    {pair.fromStatement && (
                        <Menu.Item onClick={findTransaction}>
                            Find transaction
                        </Menu.Item>
                    )}
                    {allowSwitchingToRemoval && (
                        <Menu.Item onClick={switchToRemoval} destructive>
                            Delete transaction
                        </Menu.Item>
                    )}
                    {allowSwitchingToNotReconciled && (
                        <Menu.Item onClick={switchToNotReconciled}>
                            Mark unreconciled
                        </Menu.Item>
                    )}
                    {allowSwitchingToFromPreviousPeriod && (
                        <Menu.Item onClick={switchToFromPreviousPeriod}>
                            Move to prior period
                        </Menu.Item>
                    )}
                    {canRejectDateChange && (
                        <Menu.Item onClick={toggleDateChangeIgnoring}>
                            {dateChangeIsIgnored
                                ? "Match statement date"
                                : "Reject date change"}
                        </Menu.Item>
                    )}
                </Menu.More>
            )}
        </>
    );
};
